import { apiClient, API_BASE_URL } from './httpClient';

const API = `${API_BASE_URL}/api`;

const api = {
  // ==================== USER ENDPOINTS ====================
  createUser: async (data) => {
    const response = await apiClient.post('/api/users', data);
    return response.data;
  },

  getUser: async (userId) => {
    const response = await apiClient.get(`/api/users/${userId}`);
    return response.data;
  },

  // ==================== AUDIOMETRIC TEST ENDPOINTS ====================
  createAudiometricTest: async (data) => {
    const response = await apiClient.post('/api/audiometric-tests', data);
    return response.data;
  },

  getAudiometricTests: async (userId) => {
    const response = await apiClient.get(`/api/audiometric-tests/user/${userId}`);
    return response.data;
  },

  getAudiometricTest: async (testId) => {
    const response = await apiClient.get(`/api/audiometric-tests/${testId}`);
    return response.data;
  },

  deleteAudiometricTest: async (testId) => {
    const response = await apiClient.delete(`/api/audiometric-tests/${testId}`);
    return response.data;
  },

  // ==================== TINNITUS TEST ENDPOINTS ====================
  
  // P1: Check if user has a test from today
  getTodayTinnitusTest: async (userId) => {
    const response = await apiClient.get(`/api/tinnitus-tests/today/${userId}`);
    return response.data;
  },
  
  createTinnitusTest: async (data, replaceToday = false) => {
    const response = await apiClient.post(`/api/tinnitus-tests?replace_today=${replaceToday}`, data);
    return response.data;
  },

  getTinnitusTests: async (userId) => {
    const response = await apiClient.get(`/api/tinnitus-tests/user/${userId}`);
    return response.data;
  },

  getLatestTinnitusTest: async (userId) => {
    const response = await apiClient.get(`/api/tinnitus-tests/latest/${userId}`);
    return response.data;
  },

  deleteTinnitusTest: async (testId) => {
    const response = await apiClient.delete(`/api/tinnitus-tests/${testId}`);
    return response.data;
  },

  // ==================== THERAPY SESSION ENDPOINTS ====================
  createTherapySession: async (data) => {
    const response = await apiClient.post(`/api/therapy-sessions`, data);
    return response.data;
  },

  getTherapySessions: async (userId) => {
    const response = await apiClient.get(`/api/therapy-sessions/user/${userId}`);
    return response.data;
  },

  getTherapyStats: async (userId) => {
    const response = await apiClient.get(`/api/therapy-sessions/stats/${userId}`);
    return response.data;
  },

  // ==================== CUSTOM SOUNDS ENDPOINTS ====================
  uploadCustomSound: async (userId, name, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(
      `/api/custom-sounds/upload?user_id=${userId}&name=${encodeURIComponent(name)}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  getCustomSounds: async (userId) => {
    const response = await apiClient.get(`/api/custom-sounds/user/${userId}`);
    return response.data;
  },

  getCustomSoundStreamUrl: (soundId) => {
    return `/api/custom-sounds/${soundId}/stream`;
  },

  deleteCustomSound: async (soundId) => {
    const response = await apiClient.delete(`/api/custom-sounds/${soundId}`);
    return response.data;
  },

  // ==================== EQUALIZER ENDPOINT ====================
  getUserEqualizer: async (userId) => {
    const response = await apiClient.get(`/api/equalizer/${userId}`);
    return response.data;
  },

  // ==================== SOUND CATEGORIES ENDPOINTS ====================
  getSoundCategories: async () => {
    const response = await apiClient.get(`/api/sounds/categories`);
    return response.data;
  },

  getSoundsByCategory: async (category) => {
    const response = await apiClient.get(`/api/sounds/${category}`);
    return response.data;
  },

  // ==================== USER LIMITS (V1.1) ====================
  getUserLimits: async () => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.get(`/api/user/limits`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // ==================== FAVORITES ENDPOINTS ====================
  addFavorite: async (sound) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    try {
      const response = await apiClient.post(`/api/favorites/add`, sound, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      // V1.1: Handle limit error with structured response
      if (error.response?.status === 403 && error.response?.data?.detail?.error_code === 'FAVORITES_LIMIT_REACHED') {
        const detail = error.response.data.detail;
        const lang = localStorage.getItem('hearen_lang') || 'fr';
        throw {
          type: 'LIMIT_REACHED',
          code: detail.error_code,
          message: lang === 'en' ? detail.message_en : detail.message_fr,
          current: detail.current_count,
          limit: detail.limit
        };
      }
      throw error;
    }
  },

  removeFavorite: async (soundPath) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.delete(`/api/favorites/remove`, {
      params: { sound_path: soundPath },
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getFavorites: async () => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.get(`/api/favorites`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // ==================== PLAYLISTS ENDPOINTS ====================
  createPlaylist: async (name, sounds = []) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    try {
      const response = await apiClient.post(`/api/playlists/create`, 
        { name, sounds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      // V1.1: Handle limit error with structured response
      if (error.response?.status === 403 && error.response?.data?.detail?.error_code === 'PLAYLIST_LIMIT_REACHED') {
        const detail = error.response.data.detail;
        const lang = localStorage.getItem('hearen_lang') || 'fr';
        throw {
          type: 'LIMIT_REACHED',
          code: detail.error_code,
          message: lang === 'en' ? detail.message_en : detail.message_fr,
          current: detail.current_count,
          limit: detail.limit
        };
      }
      throw error;
    }
  },

  // V1.1: Ensure default playlist exists for new users
  // Uses a promise cache to prevent race conditions from multiple calls
  _ensureDefaultPromise: null,
  ensureDefaultPlaylist: async () => {
    // Prevent duplicate calls (race condition protection)
    if (api._ensureDefaultPromise) {
      return api._ensureDefaultPromise;
    }
    
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const userLang = localStorage.getItem('hearen_lang') || 'fr';
    
    api._ensureDefaultPromise = apiClient.post(`/api/playlists/ensure-default?lang=${userLang}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      // Reset promise after 5 seconds to allow future calls (e.g., page reload)
      setTimeout(() => { api._ensureDefaultPromise = null; }, 5000);
      return response.data;
    }).catch(error => {
      api._ensureDefaultPromise = null;
      throw error;
    });
    
    return api._ensureDefaultPromise;
  },

  getPlaylists: async () => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const userLang = localStorage.getItem('hearen_lang') || 'fr';
    const response = await apiClient.get(`/api/playlists?lang=${userLang}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updatePlaylist: async (playlistId, updates) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.put(`/api/playlists/${playlistId}`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  deletePlaylist: async (playlistId) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.delete(`/api/playlists/${playlistId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  addSoundToPlaylist: async (playlistId, sound) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.post(`/api/playlists/${playlistId}/add-sound`, sound, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  removeSoundFromPlaylist: async (playlistId, soundPath) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.delete(`/api/playlists/${playlistId}/remove-sound`, {
      params: { sound_path: soundPath },
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // ==================== USER PROFILE & ONBOARDING ====================
  getUserProfile: async () => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.get(`/api/user/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateUserProfile: async (profileData) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.put(`/api/user/profile`, profileData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  resetOnboarding: async () => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.delete(`/api/user/profile/onboarding`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  changePassword: async (data) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await apiClient.post(`/api/auth/change-password`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // ==================== HEALTH CHECK ====================
  healthCheck: async () => {
    const response = await apiClient.get(`/api/health`);
    return response.data;
  },
};

export default api;
